import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CompanyPhoneNumber, RecordingNoticeMode } from '@/api/company'
import { rafraichirTelephone } from '@/api/voice'

export type UpdatablePhoneAssignment = 'main' | 'unassigned'

export interface UpdatePhoneNumberInput {
  label?: string
  assignment_type?: UpdatablePhoneAssignment
}

/**
 * Payload pour PATCH /companies/{id}/phone-numbers/{id}/settings/.
 * Tous les champs optionnels (partial update). Si l'un des 5 champs adresse
 * change, le backend cree une nouvelle Twilio Address et update le binding.
 */
export interface PhoneNumberSettingsInput {
  voice?: string
  record_inbound_calls?: boolean
  record_outbound_calls?: boolean
  recordings_retention_days?: number
  recording_notice_mode?: RecordingNoticeMode
  recording_notice_message?: string | null
  transcription_enabled?: boolean
  ai_summary_enabled?: boolean
  // Adresse E911 par-numéro.
  address1?: string | null
  address2?: string | null
  city?: string | null
  province?: string | null
  postal?: string | null
}

/** PATCH /api/companies/{companyId}/phone-numbers/{numberId}/ — label + assignment. */
export function useUpdatePhoneNumber(
  companyId: number | undefined,
  numberId: number,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdatePhoneNumberInput) => {
      const { data } = await api.patch<CompanyPhoneNumber>(
        `/api/companies/${companyId}/phone-numbers/${numberId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['company-phone-numbers', companyId],
      })
    },
  })
}

/**
 * PATCH /api/companies/{companyId}/phone-numbers/{numberId}/settings/
 * Met à jour la configuration par-numéro (voice, recording, transcription, ai_summary).
 */
export function useUpdatePhoneNumberSettings(
  companyId: number | undefined,
  numberId: number,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: PhoneNumberSettingsInput) => {
      const { data } = await api.patch<CompanyPhoneNumber>(
        `/api/companies/${companyId}/phone-numbers/${numberId}/settings/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['company-phone-numbers', companyId],
      })
    },
  })
}

/**
 * DELETE /api/numbers/release/ — libère un numéro chez Twilio.
 * Pattern Next : on envoie le Twilio SID dans le body, pas l'id Django.
 */
export function useReleasePhoneNumber(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (numberSid: string) => {
      await api.delete('/api/numbers/release/', {
        data: { numberSid },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['company-phone-numbers', companyId],
      })
      rafraichirTelephone(queryClient)
    },
  })
}

/** Formate +1XXXXXXXXXX → +1 (XXX) XXX-XXXX (pattern Next). */
export function formatPhoneNumber(phone: string): string {
  if (phone.startsWith('+1') && phone.length === 12) {
    const d = phone.substring(2)
    return `+1 (${d.substring(0, 3)}) ${d.substring(3, 6)}-${d.substring(6)}`
  }
  return phone
}

export type TollfreeSmsStatus = 'approved' | 'pending' | 'none' | 'rejected' | 'unknown'

export interface TollfreeSmsState {
  status: TollfreeSmsStatus
  toll_free: boolean
  reason?: string
  prefill?: TollfreeSmsForm
  use_cases?: string[]
  volumes?: string[]
}

export interface TollfreeSmsForm {
  business_name: string
  business_number?: string
  business_email?: string
  website: string
  address1: string
  address2?: string
  city: string
  province: string
  postal: string
  country?: string
  contact_first_name: string
  contact_last_name: string
  contact_email: string
  contact_phone: string
  use_case?: string
  use_case_summary?: string
  sample_message?: string
  message_volume?: string
}

/** GET .../phone-numbers/{id}/tollfree-sms/ : où en est la vérification pour les textos. */
export function useTollfreeSms(companyId: number | undefined, numberId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['tollfree-sms', companyId, numberId],
    queryFn: async () => {
      const { data } = await api.get<TollfreeSmsState>(`/api/companies/${companyId}/phone-numbers/${numberId}/tollfree-sms/`)
      return data
    },
    enabled: !!companyId && !!numberId && enabled,
    staleTime: 60_000,
  })
}

/** POST .../tollfree-sms/ : dépose le dossier auprès des opérateurs. */
export function useSubmitTollfreeSms(companyId: number | undefined, numberId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (form: TollfreeSmsForm) => {
      const { data } = await api.post<{ status: TollfreeSmsStatus }>(`/api/companies/${companyId}/phone-numbers/${numberId}/tollfree-sms/`, form)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tollfree-sms', companyId, numberId] })
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers', companyId] })
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] })
    },
  })
}

// =============================================================================
// Heures d'ouverture par numéro (suit l'entreprise par défaut)
// =============================================================================
export type JourHeures = { open: string; close: string }
export interface HeuresNumero {
  use_company_hours: boolean
  days: Record<'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun', JourHeures>
}

export function useHeuresNumero(companyId: number | undefined, numberId: number | undefined) {
  return useQuery({
    queryKey: ['phone-number-schedule', companyId, numberId],
    queryFn: async () => {
      const { data } = await api.get<HeuresNumero>(`/api/companies/${companyId}/phone-numbers/${numberId}/schedule/`)
      return data
    },
    enabled: !!companyId && !!numberId,
  })
}

export function useModifierHeuresNumero(companyId: number | undefined, numberId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<HeuresNumero>) => {
      const { data } = await api.put<HeuresNumero>(`/api/companies/${companyId}/phone-numbers/${numberId}/schedule/`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-number-schedule', companyId, numberId] })
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
    },
  })
}
